import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import {
  AprilRelance,
  RelancesAprilService
} from '../services/relances-april.service';

@Component({
  selector: 'app-relances-april',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    TableModule,
    TagModule,
    TooltipModule
  ],
  templateUrl: './relances-april.component.html',
  styleUrl: './relances-april.component.css'
})
export class RelancesAprilComponent implements OnInit {
  relances: AprilRelance[] = [];
  loading = false;
  error: string | null = null;
  info: string | null = null;

  subject = '';
  weeks = 3;
  maxSends = 9;
  /** Mise en route : les mails APRIL envoyes avant ne sont jamais relances. */
  activationAt: string | null = null;

  activeCount = 0;
  stoppedCount = 0;
  finishedCount = 0;
  failedCount = 0;

  searchTerm = '';
  selectedStatus: string | null = null;
  statusOptions = [
    { label: 'Tous les statuts', value: null },
    { label: 'En cours', value: 'active' },
    { label: 'Stoppées', value: 'stopped' },
    { label: 'Terminées', value: 'finished' },
    { label: 'En erreur', value: 'failed' }
  ];

  /** Ids en cours d'action, pour ne desactiver que le bouton concerne. */
  pendingIds = new Set<number>();

  /** Scan Gmail en cours : n'immobilise pas le tableau, deja affiche. */
  syncing = false;

  constructor(private relancesService: RelancesAprilService) {}

  ngOnInit(): void {
    this.refresh();
    // Scan Gmail automatique : sans lui, un mail envoye entre deux creneaux du
    // cron n'apparaitrait qu'au passage suivant, jusqu'a 3 jours plus tard.
    // Lance en tache de fond, la liste s'affiche sans attendre.
    this.sync(true);
  }

  refresh(): void {
    this.loading = true;
    this.error = null;

    this.relancesService.list(this.selectedStatus, this.searchTerm).subscribe({
      next: (response) => {
        this.relances = response.items;
        this.activeCount = response.active_count;
        this.stoppedCount = response.stopped_count;
        this.finishedCount = response.finished_count;
        this.failedCount = response.failed_count;
        this.subject = response.subject;
        this.weeks = response.weeks;
        this.maxSends = response.max_sends;
        this.activationAt = response.activation_at ?? null;
        this.loading = false;
      },
      error: (err) => {
        this.error = this.readError(err, 'Impossible de charger les relances.');
        this.loading = false;
      }
    });
  }

  applyFilters(): void {
    this.refresh();
  }

  /**
   * Scanne les « Envoyes » Gmail et inscrit les destinataires trouves.
   *
   * `auto` = declenche au chargement de la page : on reste discret, le bandeau
   * ne s'affiche que si quelque chose a reellement ete inscrit. Un clic manuel
   * rend compte du resultat dans tous les cas.
   */
  sync(auto = false): void {
    if (this.syncing) {
      return;
    }
    this.syncing = true;
    if (!auto) {
      this.error = null;
      this.info = null;
    }

    this.relancesService.sync().subscribe({
      next: (result) => {
        this.syncing = false;
        if (!auto || result.enrolled) {
          this.info =
            `${result.enrolled} relance(s) inscrite(s), ${result.skipped} déjà suivie(s).`;
          // Rend visible le « on part de zero » : sans ca, un scan qui n'inscrit
          // rien sur une boite pleine passerait pour un bug.
          if (result.ignored_before_activation) {
            this.info +=
              ` ${result.ignored_before_activation} mail(s) antérieur(s) à la mise` +
              ` en route ignoré(s).`;
          }
        }
        // Les erreurs de scan (scope Gmail manquant) remontent toujours :
        // silencieuses, elles laisseraient croire a une boite vide.
        if (result.errors?.length) {
          this.error = result.errors.join(' | ');
        }
        this.refresh();
      },
      error: (err) => {
        this.syncing = false;
        this.error = this.readError(err, 'Synchronisation Gmail impossible.');
      }
    });
  }

  stop(relance: AprilRelance): void {
    const label = relance.recipient_name || relance.recipient_email;
    if (!confirm(`Arreter les relances pour ${label} ? Les autres continueront.`)) {
      return;
    }

    this.pendingIds.add(relance.id);
    this.error = null;

    this.relancesService.stop(relance.id).subscribe({
      next: (updated) => {
        this.applyUpdate(updated);
        this.pendingIds.delete(relance.id);
        this.info = `Relances arretees pour ${label}.`;
      },
      error: (err) => {
        this.error = this.readError(err, "Impossible d'arreter cette relance.");
        this.pendingIds.delete(relance.id);
      }
    });
  }

  resume(relance: AprilRelance): void {
    this.pendingIds.add(relance.id);
    this.error = null;

    this.relancesService.resume(relance.id).subscribe({
      next: (updated) => {
        this.applyUpdate(updated);
        this.pendingIds.delete(relance.id);
        this.info = `Relances reprises pour ${updated.recipient_email}.`;
      },
      error: (err) => {
        this.error = this.readError(err, 'Impossible de reprendre cette relance.');
        this.pendingIds.delete(relance.id);
      }
    });
  }

  isPending(relance: AprilRelance): boolean {
    return this.pendingIds.has(relance.id);
  }

  statusLabel(status: string): string {
    switch (status) {
      case 'active':
        return 'En cours';
      case 'stopped':
        return 'Stoppée';
      case 'finished':
        return 'Terminée';
      case 'failed':
        return 'Erreur';
      default:
        return status;
    }
  }

  statusSeverity(status: string): string {
    switch (status) {
      case 'active':
        return 'success';
      case 'stopped':
        // PrimeNG 19 : la severite s'appelle « warn », plus « warning ».
        return 'warn';
      case 'finished':
        return 'info';
      case 'failed':
        return 'danger';
      default:
        return 'info';
    }
  }

  /** Jours restants avant la fin de la fenetre de 3 semaines. */
  daysLeft(relance: AprilRelance): number | null {
    if (!relance.campaign_ends_at) {
      return null;
    }
    const end = new Date(relance.campaign_ends_at).getTime();
    const diff = end - Date.now();
    return diff <= 0 ? 0 : Math.ceil(diff / (1000 * 60 * 60 * 24));
  }

  private applyUpdate(updated: AprilRelance): void {
    const index = this.relances.findIndex((item) => item.id === updated.id);
    if (index >= 0) {
      this.relances[index] = updated;
      this.relances = [...this.relances];
    }
    this.recomputeCounts();
  }

  private recomputeCounts(): void {
    this.activeCount = this.relances.filter((r) => r.status === 'active').length;
    this.stoppedCount = this.relances.filter((r) => r.status === 'stopped').length;
    this.finishedCount = this.relances.filter((r) => r.status === 'finished').length;
    this.failedCount = this.relances.filter((r) => r.status === 'failed').length;
  }

  private readError(err: any, fallback: string): string {
    const detail = err?.error?.detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (detail?.message) {
      return detail.message;
    }
    return fallback;
  }
}
